import { useState, useEffect, useMemo } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import LoadingButton from "@mui/lab/LoadingButton";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Close from "@mui/icons-material/Close";
import EventBusy from "@mui/icons-material/EventBusy";
import Delete from "@mui/icons-material/Delete";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import dayjs from "dayjs";
import api from "../../actprep-ui/utils/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";
import layout from "../../actprep-ui/constants/layout";
import { useConfirm } from "material-ui-confirm";
import { fullName } from "../../actprep-ui/utils/formatting";
import { standardDateFormat } from "../../actprep-ui/utils/calendar";

export default function EdgeDrawer({
  open,
  onClose,
  edge,
  mode,
  coach, // eslint-disable-line no-unused-vars
  asOfDate,
  onSave,
}) {
  const [formData, setFormData] = useState({
    supervisor: null,
    subordinate: null,
    start_date: dayjs(),
    end_date: null,
    is_active: true,
  });
  const [dateRange, setDateRange] = useState([dayjs(), null]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const confirm = useConfirm();

  const isEditing = !!edge?.uuid;
  const isCreatingSupervisorEdge = mode === "supervisor";
  const isCreatingSubordinateEdge = mode === "subordinate";

  useEffect(() => {
    if (open) {
      if (edge) {
        const supervisorLabel = edge.supervisor
          ? `${fullName(edge.supervisor.coach.user)} as ${
              edge.supervisor.role
            } [${standardDateFormat(dayjs(edge.supervisor.start_date))} - ${
              edge.supervisor.end_date
                ? standardDateFormat(dayjs(edge.supervisor.end_date))
                : "Ongoing"
            }]`
          : null;
        const subordinateLabel = edge.subordinate
          ? `${fullName(edge.subordinate.coach.user)} as ${
              edge.subordinate.role
            } [${standardDateFormat(dayjs(edge.subordinate.start_date))} - ${
              edge.subordinate.end_date
                ? standardDateFormat(dayjs(edge.subordinate.end_date))
                : "Ongoing"
            }]`
          : null;
        if (isEditing) {
          // Editing existing edge
          setFormData({
            supervisor: {
              uuid: edge.supervisor.uuid,
              label: supervisorLabel,
              data: {
                start_date: edge.supervisor.start_date,
                end_date: edge.supervisor.end_date,
              },
            },
            subordinate: {
              uuid: edge.subordinate.uuid,
              label: subordinateLabel,
              data: {
                start_date: edge.subordinate.start_date,
                end_date: edge.subordinate.end_date,
              },
            },
            start_date: edge.start_date ? dayjs(edge.start_date) : dayjs(),
            end_date: edge.end_date ? dayjs(edge.end_date) : null,
            is_active: edge.is_active !== undefined ? edge.is_active : true,
          });
          setDateRange([
            edge.start_date ? dayjs(edge.start_date) : dayjs(),
            edge.end_date ? dayjs(edge.end_date) : null,
          ]);
        } else {
          // Creating new edge with pre-filled role
          setFormData({
            supervisor: edge.supervisor
              ? {
                  uuid: edge.supervisor.uuid,
                  label: supervisorLabel,
                  data: {
                    start_date: edge.supervisor.start_date,
                    end_date: edge.supervisor.end_date,
                  },
                }
              : null,
            subordinate: edge.subordinate
              ? {
                  uuid: edge.subordinate.uuid,
                  label: subordinateLabel,
                  data: {
                    start_date: edge.subordinate.start_date,
                    end_date: edge.subordinate.end_date,
                  },
                }
              : null,
            start_date: dayjs(),
            end_date: null,
            is_active: true,
          });
          setDateRange([dayjs(), null]);
        }
      }
      setErrors({});
    }
  }, [open, edge, isEditing]);

  // Calculate the valid date range based on role assignments
  const validDateRange = useMemo(() => {
    if (!formData.supervisor || !formData.subordinate) {
      return { minDate: null, maxDate: null };
    }
    const supervisorStart = dayjs(formData.supervisor.data?.start_date);
    const supervisorEnd = formData.supervisor.data?.end_date
      ? dayjs(formData.supervisor.data.end_date)
      : null;
    const subordinateStart = dayjs(formData.subordinate.data?.start_date);
    const subordinateEnd = formData.subordinate.data?.end_date
      ? dayjs(formData.subordinate.data.end_date)
      : null;

    // The edge can only exist during the intersection of both role windows
    const minDate = supervisorStart.isAfter(subordinateStart)
      ? supervisorStart
      : subordinateStart;
    const maxDate =
      supervisorEnd && subordinateEnd
        ? supervisorEnd.isBefore(subordinateEnd)
          ? supervisorEnd
          : subordinateEnd
        : supervisorEnd || subordinateEnd;

    return { minDate, maxDate };
  }, [formData.supervisor, formData.subordinate]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setFormData((prev) => ({
      ...prev,
      start_date: newRange[0],
      end_date: newRange[1],
    }));
  };

  const handleEndToday = () => {
    const today = asOfDate || dayjs();
    const newRange = [formData.start_date, today];
    setDateRange(newRange);
    setFormData((prev) => ({
      ...prev,
      end_date: today,
    }));
  };

  const handleDelete = async () => {
    if (!isEditing) return;

    try {
      await confirm({
        title: "Delete Reporting Relationship",
        description:
          "Are you sure you want to permanently delete this reporting relationship? This action cannot be undone.",
        confirmationText: "Delete",
        cancellationText: "Cancel",
        confirmationButtonProps: {
          color: "error",
          variant: "contained",
        },
      });

      setDeleteLoading(true);

      const response = await api.delete(
        apiUrls.coaching.reportingEdges(edge.uuid)
      );

      // Check the success field in the response
      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason ||
            "Failed to delete reporting relationship due to validation issues"
        );
        return;
      }

      showSuccessSnackbar("Reporting relationship deleted successfully");
      onSave();
    } catch (error) {
      if (error !== undefined) {
        // User didn't cancel
        console.error("Error deleting reporting edge:", error);
        showErrorSnackbar(
          error.response?.data?.detail ||
            error.response?.data?.reason ||
            "Failed to delete reporting relationship"
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.supervisor) {
      newErrors.supervisor = "Manager role is required";
    }

    if (!formData.subordinate) {
      newErrors.subordinate = "Employee role is required";
    }

    if (
      formData.supervisor &&
      formData.subordinate &&
      formData.supervisor.uuid === formData.subordinate.uuid
    ) {
      newErrors.subordinate = "Manager and employee cannot be the same role";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (
      formData.end_date &&
      formData.start_date &&
      formData.end_date.isBefore(formData.start_date, "day")
    ) {
      newErrors.end_date = "End date must be after start date";
    }

    // Validate date range against role windows
    if (
      validDateRange.minDate &&
      formData.start_date &&
      formData.start_date.isBefore(validDateRange.minDate, "day")
    ) {
      newErrors.start_date =
        "Start date is outside the valid role overlap window";
    }

    if (
      validDateRange.maxDate &&
      formData.end_date &&
      formData.end_date.isAfter(validDateRange.maxDate, "day")
    ) {
      newErrors.end_date = "End date is outside the valid role overlap window";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        supervisor_id: formData.supervisor.uuid,
        subordinate_id: formData.subordinate.uuid,
        start_date: formData.start_date.format("YYYY-MM-DD"),
        end_date: formData.end_date
          ? formData.end_date.format("YYYY-MM-DD")
          : null,
        is_active: formData.is_active,
      };

      let response;
      if (isEditing) {
        response = await api.put(
          apiUrls.coaching.reportingEdges(edge.uuid),
          payload
        );
      } else {
        response = await api.post(apiUrls.coaching.reportingEdges(), payload);
      }

      // Check the success field in the response
      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason ||
            `Failed to ${
              isEditing ? "update" : "create"
            } reporting relationship due to data validation issues`
        );
        return;
      }

      showSuccessSnackbar(
        `Reporting relationship ${
          isEditing ? "updated" : "created"
        } successfully`
      );
      onSave();
    } catch (error) {
      console.error("Error saving reporting edge:", error);
      showErrorSnackbar(
        error.response?.data?.detail ||
          error.response?.data?.reason ||
          `Failed to ${isEditing ? "update" : "create"} reporting relationship`
      );
    } finally {
      setLoading(false);
    }
  };

  const getCounterpartFilterParams = () => {
    return {};
    // // TODO: Make this work. Will need to add compatibiliy for __ne-type stuff.
    // if (isCreatingSupervisorEdge) {
    //   // Looking for potential subordinates
    //   return {
    //     coach_id__ne: coach.uuid, // Exclude current coach
    //   };
    // } else if (isCreatingSubordinateEdge) {
    //   // Looking for potential supervisors
    //   return {
    //     coach_id__ne: coach.uuid, // Exclude current coach
    //   };
    // }
    // return {
    //   coach__ne: coach.uuid, // Exclude current coach
    // };
  };

  const drawerContent = (
    <Box sx={{ width: 500, p: 3, mt: `${layout.navBarHeight + 1}rem` }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">
          {isEditing
            ? "Edit Reporting Relationship"
            : "Add Reporting Relationship"}
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>

      <Stack spacing={3}>
        {/* Supervisor Selection */}
        <ModelAutoComplete
          modelName="CoachRoleAssignment"
          label="Manager Role"
          value={formData.supervisor}
          setValue={(value) => handleFieldChange("supervisor", value)}
          defaultParams={getCounterpartFilterParams()}
          disabled={isCreatingSupervisorEdge}
          includeFields={["start_date", "end_date"]}
          required
        />
        {errors.supervisor && (
          <Typography variant="caption" color="error">
            {errors.supervisor}
          </Typography>
        )}

        {/* Subordinate Selection */}
        <ModelAutoComplete
          modelName="CoachRoleAssignment"
          label="Employee Role"
          value={formData.subordinate}
          setValue={(value) => handleFieldChange("subordinate", value)}
          defaultParams={getCounterpartFilterParams()}
          disabled={isCreatingSubordinateEdge}
          includeFields={["start_date", "end_date"]}
          required
        />
        {errors.subordinate && (
          <Typography variant="caption" color="error">
            {errors.subordinate}
          </Typography>
        )}

        {/* Date Range Info */}
        {validDateRange.minDate && (
          <Alert severity="info">
            Valid date range: {validDateRange.minDate.format("MMM D, YYYY")} -{" "}
            {validDateRange.maxDate
              ? validDateRange.maxDate.format("MMM D, YYYY")
              : "Ongoing"}
          </Alert>
        )}

        {/* Date Range Picker */}
        <Box>
          <DateRangePicker
            startText="Start Date"
            endText="End Date"
            value={dateRange}
            onChange={handleDateRangeChange}
            minDate={validDateRange.minDate}
            maxDate={validDateRange.maxDate}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!(errors.start_date || errors.end_date),
                helperText: errors.start_date || errors.end_date,
              },
            }}
          />
        </Box>

        {/* Quick End Today Button */}
        {!formData.end_date && (
          <Button
            variant="outlined"
            startIcon={<EventBusy />}
            onClick={handleEndToday}
            size="small"
          >
            End Today
          </Button>
        )}

        {/* Active Switch */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={(e) => handleFieldChange("is_active", e.target.checked)}
            />
          }
          label="Active"
        />

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          mt={4}
        >
          {/* Delete Button (only show when editing) */}
          {isEditing && (
            <LoadingButton
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleDelete}
              loading={deleteLoading}
            >
              Delete
            </LoadingButton>
          )}

          {/* Main Action Buttons */}
          <Stack direction="row" spacing={2} sx={{ ml: "auto" }}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleSave}
              loading={loading}
            >
              {isEditing ? "Update" : "Create"}
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            boxShadow: (theme) => theme.shadows[8],
          },
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
